import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { STORE_CUSTOM_DOMAIN_QUERY_KEY } from '@/api/queries/custom-domain/key';
import {
  connectCustomDomain,
  purchaseDomain,
  removeCustomDomain,
  searchDomainAvailability,
  verifyCustomDomain,
} from './request';

export const useSearchDomainAvailability = () => {
  return useMutation({
    mutationFn: searchDomainAvailability,
    onError: (error: Error) => {
      toast.error(error.message || 'Could not check domain availability');
    },
  });
};

export const usePurchaseDomain = (
  onSuccess?: (data: Awaited<ReturnType<typeof purchaseDomain>>) => void
) => {
  return useMutation({
    mutationFn: purchaseDomain,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not start domain purchase');
    },
  });
};

export const useConnectCustomDomain = (
  onSuccess?: (data: Awaited<ReturnType<typeof connectCustomDomain>>) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: connectCustomDomain,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: [STORE_CUSTOM_DOMAIN_QUERY_KEY],
      });
      toast.success(data.response || 'Add the DNS records, then verify');
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not connect domain');
    },
  });
};

export const useVerifyCustomDomain = (
  onSuccess?: (data: Awaited<ReturnType<typeof verifyCustomDomain>>) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: verifyCustomDomain,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: [STORE_CUSTOM_DOMAIN_QUERY_KEY],
      });
      toast.success(data.response || 'Domain checked');
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not verify domain');
    },
  });
};

export const useRemoveCustomDomain = (
  onSuccess?: (data: Awaited<ReturnType<typeof removeCustomDomain>>) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeCustomDomain,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: [STORE_CUSTOM_DOMAIN_QUERY_KEY],
      });
      toast.success(data.response || 'Custom domain removed');
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not remove domain');
    },
  });
};
